<script>
  import { onMount, afterUpdate } from 'svelte';


  export let deviceId;

  let photoUrl = '';
  let filename = '';
  let deviceName = '';
  let dateStr = '';
  let timeStr = '';
  let imageWidth = 0;
  let imageHeight = 0;
  let timer;
  let isBlurred = false;
  let isUploading = false;

  let isLoading = true;
  let hasPhoto = false;
  let previousDeviceId = null;


  async function updatePhotoInfo() {
    if (!deviceId) {
      resetPhotoInfo();
      isLoading = false;
      hasPhoto = false;
      return;
    }

    isLoading = true;
    hasPhoto = false;

    try {
      const mid = String(deviceId).padStart(2, '0');
      const res = await fetch(`/api/photos/${mid}/latest?t=${Date.now()}`);

      if (!res.ok) throw new Error('응답 실패');

      const data = await res.json();
      if (!data || !data.filename || !data.url) throw new Error('데이터 없음');

      filename = data.filename;
      photoUrl = data.url;
      hasPhoto = true;
      isBlurred = true;
      imageWidth = 0;
      imageHeight = 0;

      deviceName = `machine${mid}`;

      const match = filename.match(/machine\d{2}_(\d{6})_(\d{6})\.jpg$/);
      if (match) {
        const [_, datePart, timePart] = match;
        dateStr = `20${datePart.slice(0, 2)}. ${datePart.slice(2, 4)}. ${datePart.slice(4, 6)}.`;
        timeStr = `${timePart.slice(0, 2)}:${timePart.slice(2, 4)}:${timePart.slice(4, 6)}`;
      } else {
        dateStr = '날짜 추출 실패';
        timeStr = '시간 추출 실패';
      }
    } catch (err) {
      console.error('사진 정보 오류:', err);
      resetPhotoInfo();
      hasPhoto = false;
    } finally {
      isLoading = false;
    }
  }

  function resetPhotoInfo() {
    filename = '';
    photoUrl = '';
    imageWidth = 0;
    imageHeight = 0;
    dateStr = '';
    timeStr = '';
    isBlurred = false;
  }

  function handleImgLoad(e) {
    imageWidth = e.target.naturalWidth;
    imageHeight = e.target.naturalHeight;
    setTimeout(() => {
      isBlurred = false;
    }, 100);
  }

  async function handleImageClick(e) {
    e.preventDefault();
    try {
      const res = await fetch(photoUrl, { method: 'HEAD' });
      const contentType = res.headers.get('Content-Type');
      if (res.ok && contentType?.startsWith('image/')) {
        window.open(`${photoUrl}?t=${Date.now()}`, '_blank');
      } else {
        setTimeout(async () => {
          await updatePhotoInfo();
          if (photoUrl) {
            window.open(`${photoUrl}?t=${Date.now()}`, '_blank');
          }
        }, 1000);
      }
    } catch (err) {
      console.error('이미지 확인 중 오류 발생:', err);
    }
  }

  async function startUpload() {
    const res = await fetch(`/api/photos/${deviceId}/start`, { method: 'POST' });
    if (res.ok) {
      isUploading = true;
      console.log('📤 사진 전송 시작');
    }
  }

  async function stopUpload() {
    const res = await fetch(`/api/photos/${deviceId}/stop`, { method: 'POST' });
    if (res.ok) {
      isUploading = false;
      console.log('⛔️ 사진 전송 중지');
    }
  }

  async function deleteAllPhotos() {
    const confirmed = confirm('정말로 모든 사진을 삭제하시겠습니까?');
    if (!confirmed) return;
    const res = await fetch(`/api/photos/${deviceId}/deleteAll`, { method: 'DELETE' });
    if (res.ok) {
      console.log('🗑️ 모든 사진 삭제 완료');
      resetPhotoInfo();
    }
  }

  afterUpdate(() => {
    if (deviceId && deviceId !== previousDeviceId) {
      previousDeviceId = deviceId;
      resetPhotoInfo();
      isLoading = true;
      hasPhoto = false;
      updatePhotoInfo();
    }
  });

  onMount(() => {
    updatePhotoInfo();
    timer = setInterval(updatePhotoInfo, 10000);
    return () => clearInterval(timer);
  });
</script>

<div class="page-content">
  <div class="card">
    <h2>장비 {deviceId} 사진 보기</h2>

    <div class="button-group">
      <button on:click={startUpload}>시작</button>
      <button on:click={stopUpload}>중지</button>
      <button on:click={deleteAllPhotos}>삭제</button>
    </div>

    {#if deviceId}
      {#if isLoading}
        <div class="placeholder">사진을 불러오는 중입니다...</div>
      {:else if hasPhoto}
        <div class="image-wrapper">
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <img
            src={photoUrl}
            alt="Machine {deviceId} latest"
            class:blurred={isBlurred}
            on:load={handleImgLoad}
            on:click={handleImageClick}
            style="cursor: pointer;"
          />
        </div>

        <div class="metadata">
          <p><strong>파일명:</strong> {filename}</p>
          <p><strong>장비:</strong> {deviceName}</p>
          <p><strong>날짜:</strong> {dateStr}</p>
          <p><strong>시간:</strong> {timeStr}</p>
          <p><strong>이미지 크기:</strong> {imageWidth} × {imageHeight}</p>
        </div>
      {:else}
        <div class="placeholder">장비 {deviceId}에 사진이 없습니다.</div>
      {/if}
    {:else}
      <div class="placeholder">유효한 장치가 선택되지 않았습니다.</div>
    {/if}

  </div>
</div>

<style>
  .page-content {
    margin: 2rem auto;
    max-width: 800px;
    padding: 0 1rem;
  }

  .button-group {
    display: flex;
    gap: 1rem;
    margin-bottom: 1rem;
  }
  .button-group button {
    margin-top: 12px;
    background: #7a7a6a;
    color: #ffffff;
    border: none;
    padding: 8px 12px;
    border-radius: 6px;
    cursor: pointer;;
  }
  .button-group button:hover {
    background: #5e5e4a;
  }



  .card {
    background: #fcfcf8;
    border-radius: 0.5rem;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    padding: 1.5rem;
    margin-top: 1.5rem;
  }

  .card h2 {
    margin-top: 0;
    font-size: 1.25rem;
    border-bottom: 1px solid #e0e0e0;
    padding-bottom: 0.5rem;
  }
  .image-wrapper {
    text-align: center;
    margin: 1rem 0;
    width: 100%;
    position: relative;
    overflow: hidden;
  }
  .image-wrapper img {
    display: inline-block;
    max-width: 100%;     /* 웹페이지보다 크면 축소 */
    height: auto;        /* 비율 유지 */
    border: 1px solid #ddd;
    border-radius: 0.25rem;
    transition: filter 0.6s ease;
  }
  .blurred {
    filter: blur(20px);         /* ← 여기를 조절하면 됨 */
    transition: filter 0.6s ease;
  }

  .metadata {
    margin-top: 1rem;
    font-size: 0.9rem;
    color: #333;
  }

  .metadata p {
    margin: 0.3rem 0;
  }

  .placeholder {
    padding: 2rem;
    color: #666;
    text-align: center;
  }
</style>